import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { X, Calendar, ArrowsHorizontal, Spinner, FilmStrip, Play, Pause, Crosshair, DownloadSimple, LinkSimple, CaretDown, EnvelopeSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { fmtDate } from './historicalDate';
import { ShareQrModal } from './ShareQrModal';
import { SendToClientModal } from './SendToClientModal';
import { DateSelect, DateStepper } from './HistoricalDateControls';
import { copyTextToClipboard } from '../../lib/clipboard';

// Auto-play step interval for the Timeline scrubber (ms). One step per 800 ms
// keeps motion fluid without overwhelming the Wayback tile cache between hops.
const TIMELINE_STEP_MS = 800;

// Display formatter — Wayback dates come from the API as 'YYYY-MM-DD' (ISO).
// The product is AU/UK-focused, so we surface them as 'DD/MM/YYYY' to users
// while keeping the raw ISO string for sorting, keys, and the tile URL.




/**
 * Full-map overlay that lets Enterprise users compare two historical
 * Esri "Wayback" satellite imagery snapshots side-by-side.
 *
 * Architecture:
 *  - Renders TWO Google Maps inside one absolutely-positioned container.
 *  - The bottom map shows the LEFT date's Wayback tiles.
 *  - The top map is wrapped in a div with `clip-path: inset(0 0 0 X%)` so
 *    only its right portion is visible, revealing the bottom map on the left.
 *  - Both maps stay perfectly synced via google.maps `center_changed` /
 *    `zoom_changed` listeners.
 *
 * Props:
 *  - underlyingMap: the parent MapView's google.maps.Map instance, used to
 *      seed our initial center/zoom and to write back when the user closes.
 *  - apiCall:       authenticated axios wrapper (passes auth cookies/headers).
 *  - onClose():     parent callback to dismiss compare mode.
 */
export default function HistoricalCompare({ underlyingMap, apiCall, onClose, projectId, projectName, projectAddress }) {
  const containerRef = useRef(null);
  const leftMapRef = useRef(null);
  const rightMapRef = useRef(null);
  const syncingRef = useRef(false); // guard against feedback loops

  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tileTemplate, setTileTemplate] = useState('');

  // Drag handle position as a percent (0–100) from the left edge.
  const [splitPct, setSplitPct] = useState(50);
  const draggingRef = useRef(false);
  // Compare mode: 'swipe' = vertical clip-path divider, 'timeline' = chronological
  // scrubber. NOTE: the legacy string value 'fade' is still used internally on
  // some testids and state for backward-compat with the existing regression spec
  // at /app/tests/historical_compare_test.py — user-facing label is "Timeline".
  const [mode, setMode] = useState('swipe');
  // Selected releases (left = older default, right = newest default).
  const [leftRelease, setLeftRelease] = useState(null);
  const [rightRelease, setRightRelease] = useState(null);
  // FADE / scrub state — index into `scrubRange` (chronological older→newer slice
  // between the two chosen dates). The slider thumb walks this index, and the
  // bottom map shows the Wayback snapshot at that exact date.
  const [scrubIdx, setScrubIdx] = useState(0);
  // Auto-play state for the Timeline scrubber — when true, an interval advances
  // scrubIdx by 1 per TIMELINE_STEP_MS, wrapping back to 0 at the end so the
  // user sees a continuous time-lapse loop until they hit Pause.
  const [isPlaying, setIsPlaying] = useState(false);
  // GIF export progress: null = idle, {pct, format} = exporting, 'done' = recent success.
  const [exportState, setExportState] = useState(null);
  // Dropdown menu for choosing MP4 vs GIF on the Export button.
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  // After a successful "Copy share link" we open a QR-code modal so the user
  // can show the homeowner their phone-scannable link on the spot during an
  // on-site walkthrough. `shareModal` holds the full public URL or null when closed.
  const [shareModal, setShareModal] = useState(null);
  // Send-to-client modal — open with the current scrubRange so the user can
  // type a recipient email + optional note. Build runs in the background;
  // email is dispatched as soon as the share placeholder doc lands.
  const [sendModal, setSendModal] = useState(null);

  // The chronological slice of releases between leftRelease and rightRelease,
  // ordered oldest → newest so the slider thumb moves left=old → right=new
  // regardless of which side the user picked as A or B.
  const scrubRange = useMemo(() => {
    if (!leftRelease || !rightRelease || releases.length === 0) return [];
    const a = leftRelease.date <= rightRelease.date ? leftRelease.date : rightRelease.date;
    const b = leftRelease.date <= rightRelease.date ? rightRelease.date : leftRelease.date;
    return releases
      .filter((r) => r.date >= a && r.date <= b)
      .sort((x, y) => (x.date < y.date ? -1 : x.date > y.date ? 1 : 0));
  }, [leftRelease, rightRelease, releases]);

  // Clamp scrubIdx whenever the range changes (e.g. user picks different A/B).
  useEffect(() => {
    if (scrubRange.length === 0) return;
    setScrubIdx((i) => Math.max(0, Math.min(i, scrubRange.length - 1)));
  }, [scrubRange.length]);

  const currentScrub = scrubRange[scrubIdx] || null;

  // Timeline auto-play — advance scrubIdx by 1 on a fixed interval, wrapping
  // back to 0 at the end. Auto-stops if the user switches away from Timeline
  // mode or if the chronological range becomes too short to animate.
  useEffect(() => {
    if (!isPlaying || mode !== 'fade' || scrubRange.length < 2) return;
    const id = setInterval(() => {
      setScrubIdx((i) => (i + 1) % scrubRange.length);
    }, TIMELINE_STEP_MS);
    return () => clearInterval(id);
  }, [isPlaying, mode, scrubRange.length]);

  // Hard-stop auto-play whenever the user leaves Timeline mode. Keeps the UI
  // consistent: come back later and the button starts in the paused state.
  useEffect(() => {
    if (mode !== 'fade' && isPlaying) setIsPlaying(false);
  }, [mode, isPlaying]);

  // Initial view (cloned from the underlying map so the overlay opens "in place").
  // NOTE: this useMemo MUST be declared BEFORE `recenterToInitialView` below, or
  // React's render pass will throw a TDZ ReferenceError on the [initialView]
  // deps array (regression caught by iteration_16).
  //
  // IMPORTANT — Wayback zoom cap:
  // Esri's Wayback tile server (`wayback.maptiles.arcgis.com`) only serves PNG
  // tiles up to zoom ~20 for most regional areas. At zoom 21+ the upstream
  // returns HTML/JSON 404 pages, which browsers (Chrome / Firefox CORB and
  // ORB) then block as cross-origin "opaque" responses — the result is a
  // completely blank compare view with no console error. Old projects that
  // the user has zoomed deeper (saved via debounced onSaveLocation) inherit
  // the deeper zoom into Time Travel and trigger this. New projects default
  // to zoom 20, which is why a freshly-created project at the same address
  // works while the old one doesn't (see bug iteration ~38).
  // Clamp the initial zoom to 20 so the compare maps always start in a range
  // Wayback can serve. The user can zoom further with the Google controls;
  // tiles above 20 will still return blank but the compare will at least
  // mount with imagery visible at the entry zoom.
  const WAYBACK_MAX_ZOOM = 20;
  const initialView = useMemo(() => {
    if (!underlyingMap) return { center: { lat: -33.8688, lng: 151.2093 }, zoom: 18 };
    const c = underlyingMap.getCenter();
    const rawZoom = underlyingMap.getZoom() || 18;
    return {
      center: c ? { lat: c.lat(), lng: c.lng() } : { lat: -33.8688, lng: 151.2093 },
      zoom: Math.min(rawZoom, WAYBACK_MAX_ZOOM),
    };
  }, [underlyingMap]);

  // Reset both maps' center/zoom back to where the underlying MapView was when
  // the overlay opened. Useful after the user has panned far away while
  // scrubbing dates and wants to snap back to the property they came from.
  const recenterToInitialView = useCallback(() => {
    if (!window.google?.maps) return;
    [leftMapRef.current, rightMapRef.current].forEach((m) => {
      if (!m) return;
      try {
        m.setCenter(initialView.center);
        m.setZoom(initialView.zoom);
      } catch (e) { console.warn('recenter failed:', e); }
    });
  }, [initialView]);

  // Trigger a server-side time-lapse export of the current Timeline range in
  // the requested container format ('gif' | 'mp4'), centred on the LEFT map's
  // current view (so the user can pan to frame the property exactly how they
  // want it before exporting). Server enforces a 16-frame cap and
  // Enterprise-only access via require_enterprise(). MP4 is roughly 5–10×
  // smaller than the equivalent GIF and avoids 256-colour banding.
  const handleExportMedia = useCallback(async (fmt) => {
    const format = fmt === 'mp4' ? 'mp4' : 'gif';
    if (scrubRange.length < 2 || exportState) return;
    const map = leftMapRef.current;
    if (!map) return;
    let centerLat = initialView.center.lat;
    let centerLng = initialView.center.lng;
    let zoom = initialView.zoom;
    let viewportW = 0;
    let viewportH = 0;
    try {
      const c = map.getCenter();
      if (c) { centerLat = c.lat(); centerLng = c.lng(); }
      const z = map.getZoom();
      if (typeof z === 'number') zoom = z;
      // Read the map div's actual pixel dimensions so the exported file matches
      // what the user sees on screen rather than a fixed 768×768 thumbnail.
      const div = map.getDiv?.();
      if (div) {
        viewportW = Math.round(div.clientWidth || 0);
        viewportH = Math.round(div.clientHeight || 0);
      }
    } catch (_) { /* fall back to initialView */ }
    setIsPlaying(false); // pause auto-play during export so the slider doesn't fight us
    setExportMenuOpen(false);
    setExportState({ pct: 0, format });
    try {
      const release_ids = scrubRange.map((r) => r.release_id);
      const startDate = (scrubRange[0]?.date || '').replace(/-/g, '');
      const endDate = (scrubRange[scrubRange.length - 1]?.date || '').replace(/-/g, '');
      // Short, URL-safe project slug. Trimmed to 24 chars so the final filename
      // stays comfortably under typical OS limits.
      const projectSlug = (projectName || '')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 24);
      const filename = projectSlug
        ? `roofmate-${projectSlug}-timetravel-${startDate}-${endDate}`
        : `roofmate-timetravel-${startDate}-${endDate}`;
      const body = {
        center_lat: centerLat,
        center_lng: centerLng,
        zoom: Math.round(zoom),
        release_ids,
        filename,
        output_format: format,
      };
      if (viewportW >= 256 && viewportH >= 256) {
        body.viewport_width = viewportW;
        body.viewport_height = viewportH;
      }
      // Watermark metadata — backend renders these subtly in the bottom-right
      // of every frame so shared media remain self-identifying.
      if (projectName) body.project_name = projectName;
      if (projectAddress) body.property_address = projectAddress;
      if (projectId) body.project_id = projectId;
      // Use apiCall with responseType:'blob' so the raw video/image bytes are
      // returned. The fetch-based binary path inside AuthContext handles the
      // platform-injected XHR monitor that previously crashed on blobs.
      // Single auto-retry on transient failures (502/504/network/timeout) —
      // the /export endpoint is synchronous and very occasionally exceeds the
      // ingress 60-s timeout for large viewports, so one quiet retry recovers
      // most of the "sometimes fails" cases without surfacing an error.
      let res;
      let lastErr = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          res = await apiCall('post', '/wayback/export', body, { responseType: 'blob' });
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          // Only retry transient ingress/network errors. Don't retry 4xx
          // (validation/auth/plan) — those are deterministic and would just
          // burn time. Also don't retry if the user has cancelled (no map).
          const isTransient =
            !status ||              // network error / fetch abort
            status === 0 ||
            status === 502 ||
            status === 503 ||
            status === 504 ||
            status === 408;
          if (attempt === 0 && isTransient) {
            // Quick exponential pause before the second attempt — gives the
            // upstream Esri CDN a beat to recover from a burst-limit blip.
            await new Promise((r) => setTimeout(r, 1500));
            continue;
          }
          break;
        }
      }
      if (lastErr) throw lastErr;
      const mime = format === 'mp4' ? 'video/mp4' : 'image/gif';
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      setExportState('done');
      setTimeout(() => setExportState(null), 2500);
    } catch (e) {
      const status = e?.response?.status;
      const isTransient = !status || status === 502 || status === 503 || status === 504 || status === 408;
      const detail = e?.response?.data?.detail
        || (e?.response?.data instanceof Blob ? `${format.toUpperCase()} export failed — try a different zoom or fewer frames.` : null)
        || (isTransient ? `${format.toUpperCase()} export timed out. Try again, or pick a smaller date range.` : null)
        || e?.message
        || `${format.toUpperCase()} export failed.`;
      console.warn('Wayback export failed:', detail);
      setExportState(null);
      toast.error(typeof detail === 'string' ? detail : `${format.toUpperCase()} export failed.`, {
        // Inline "Retry" action button — re-invokes the same handler so the
        // user doesn't have to reopen the menu.
        action: {
          label: 'Retry',
          onClick: () => { handleExportMediaRef.current?.(format); },
        },
        duration: 8000,
      });
    }
  }, [scrubRange, exportState, initialView, apiCall, projectName, projectAddress]);
  // Stable ref so the toast's Retry button can call the latest handleExportMedia
  // closure without forcing a re-render of every toast component.
  const handleExportMediaRef = useRef(null);
  useEffect(() => { handleExportMediaRef.current = handleExportMedia; }, [handleExportMedia]);

  // Build a shareable MP4 of the current Timeline range, store it server-side
  // for 7 days, and copy the resulting public URL to the clipboard. This is
  // the "drop it in a client email or Slack" path — much faster than asking
  // users to download an MP4 then re-upload it somewhere else.
  // We always export MP4 here (regardless of which format the user has
  // previously downloaded) because MP4 renders inline in Slack/Gmail and is
  // dramatically smaller than the equivalent GIF.
  const handleCopyShareLink = useCallback(async () => {
    if (scrubRange.length < 2 || exportState) return;
    const map = leftMapRef.current;
    if (!map) return;
    let centerLat = initialView.center.lat;
    let centerLng = initialView.center.lng;
    let zoom = initialView.zoom;
    let viewportW = 0;
    let viewportH = 0;
    try {
      const c = map.getCenter();
      if (c) { centerLat = c.lat(); centerLng = c.lng(); }
      const z = map.getZoom();
      if (typeof z === 'number') zoom = z;
      const div = map.getDiv?.();
      if (div) {
        viewportW = Math.round(div.clientWidth || 0);
        viewportH = Math.round(div.clientHeight || 0);
      }
    } catch (_) { /* fall back to initialView */ }
    setIsPlaying(false);
    setExportMenuOpen(false);
    setExportState({ pct: 0, format: 'share' });
    try {
      const release_ids = scrubRange.map((r) => r.release_id);
      const startDate = (scrubRange[0]?.date || '').replace(/-/g, '');
      const endDate = (scrubRange[scrubRange.length - 1]?.date || '').replace(/-/g, '');
      const projectSlug = (projectName || '')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 24);
      const filename = projectSlug
        ? `roofmate-${projectSlug}-timetravel-${startDate}-${endDate}`
        : `roofmate-timetravel-${startDate}-${endDate}`;
      const body = {
        center_lat: centerLat,
        center_lng: centerLng,
        zoom: Math.round(zoom),
        release_ids,
        filename,
        output_format: 'mp4',
      };
      if (viewportW >= 256 && viewportH >= 256) {
        body.viewport_width = viewportW;
        body.viewport_height = viewportH;
      }
      if (projectName) body.project_name = projectName;
      if (projectAddress) body.property_address = projectAddress;
      if (projectId) body.project_id = projectId;
      const res = await apiCall('post', '/wayback/share', body);
      const path = res?.data?.url;
      if (!path) throw new Error('Server did not return a share URL.');
      // The backend builds the file in a background task and returns the
      // public URL immediately (status='building'). The link the user copies
      // is shareable right away — recipients will see a "preparing video"
      // page until the build completes (typically <2 minutes for realistic
      // viewports) and then the actual MP4/GIF inline.
      const isBuilding = res?.data?.status === 'building';
      // Construct the absolute URL from REACT_APP_BACKEND_URL so the link
      // works when pasted into another app outside this preview origin.
      const origin = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const fullUrl = `${origin}${path}`;
      // Try the modern clipboard API; some browsers / iframes block it, so
      // fall back to a hidden textarea + execCommand('copy').
      const copied = await copyTextToClipboard(fullUrl);
      setExportState('done');
      setTimeout(() => setExportState(null), 2500);
      // Open the QR-code modal so the user can either pass the homeowner's
      // phone scanned the link in person, or copy the URL again from the dialog.
      // We capture `isBuilding` so the modal can show the right ETA copy.
      setShareModal({ url: fullUrl, isBuilding, copied });
      if (copied) {
        toast.success('Share link copied to clipboard', {
          description: isBuilding
            ? 'QR code shown — scan or paste anywhere. Recipients see a "preparing video" page for ~30–60 s, then the MP4. Valid 7 days.'
            : 'QR code shown — anyone with the link or scan can view this time-lapse for the next 7 days.',
        });
      } else {
        // Clipboard blocked — the modal below still shows the URL + a manual
        // Copy button so the user isn't left without a way to grab the link.
        toast.message('Share link ready — see QR code', {
          description: 'Clipboard access was blocked, but you can scan the QR or copy the URL from the dialog.',
          duration: 8000,
        });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail
        || e?.message
        || 'Could not create share link.';
      console.warn('Wayback share failed:', detail);
      setExportState(null);
      toast.error(typeof detail === 'string' ? detail : 'Could not create share link.');
    }
  }, [scrubRange, exportState, initialView, apiCall, projectName, projectAddress, projectId]);

  // Open the Send-to-client modal. The actual share build + email send happen
  // inside the modal after the user has typed a recipient email + note, so we
  // can validate input before kicking off the (slow) build.
  const handleSendToClient = useCallback(() => {
    if (scrubRange.length < 2 || exportState) return;
    setExportMenuOpen(false);
    setSendModal({
      // Pass the current map view + release IDs as a snapshot so even if the
      // user scrubs the timeline after opening the modal, the email reflects
      // what they saw when they clicked Send.
      payloadBuilder: () => {
        const map = leftMapRef.current;
        let centerLat = initialView.center.lat;
        let centerLng = initialView.center.lng;
        let zoom = initialView.zoom;
        let viewportW = 0;
        let viewportH = 0;
        try {
          const c = map?.getCenter();
          if (c) { centerLat = c.lat(); centerLng = c.lng(); }
          const z = map?.getZoom();
          if (typeof z === 'number') zoom = z;
          const div = map?.getDiv?.();
          if (div) {
            viewportW = Math.round(div.clientWidth || 0);
            viewportH = Math.round(div.clientHeight || 0);
          }
        } catch (_) { /* fall back to initialView */ }
        const release_ids = scrubRange.map((r) => r.release_id);
        const startDate = (scrubRange[0]?.date || '').replace(/-/g, '');
        const endDate = (scrubRange[scrubRange.length - 1]?.date || '').replace(/-/g, '');
        const projectSlug = (projectName || '')
          .toLowerCase()
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 24);
        const filename = projectSlug
          ? `roofmate-${projectSlug}-timetravel-${startDate}-${endDate}`
          : `roofmate-timetravel-${startDate}-${endDate}`;
        const body = {
          center_lat: centerLat,
          center_lng: centerLng,
          zoom: Math.round(zoom),
          release_ids,
          filename,
          output_format: 'mp4',
        };
        if (viewportW >= 256 && viewportH >= 256) {
          body.viewport_width = viewportW;
          body.viewport_height = viewportH;
        }
        if (projectName) body.project_name = projectName;
        if (projectAddress) body.property_address = projectAddress;
        if (projectId) body.project_id = projectId;
        return body;
      },
      projectName,
      projectAddress,
    });
  }, [scrubRange, exportState, initialView, projectName, projectAddress, projectId]);

  // Close the format-picker dropdown when the user clicks anywhere outside it.
  // Mirrors the DateSelect pattern further down in this file.
  useEffect(() => {
    if (!exportMenuOpen) return;
    const onDoc = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [exportMenuOpen]);

  // When switching back to Swipe mode after the right map was display:none'd
  // in Fade, Google Maps needs a manual `resize` ping to repaint its tiles
  // into the now-visible container.
  useEffect(() => {
    if (mode !== 'swipe' || !rightMapRef.current || !window.google?.maps) return;
    // Wait one frame so the wrapper div is back in the layout flow.
    const id = requestAnimationFrame(() => {
      try { window.google.maps.event.trigger(rightMapRef.current, 'resize'); } catch (_) { /* harmless */ }
    });
    return () => cancelAnimationFrame(id);
  }, [mode]);

  // ----- Fetch releases on mount -----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiCall('get', '/wayback/releases');
        if (cancelled) return;
        const list = res.data?.releases || [];
        setReleases(list);
        setTileTemplate(res.data?.tile_url_template || '');
        // Sensible defaults: newest on the right, ~3 years older on the left.
        if (list.length > 0) {
          setRightRelease(list[0]);
          // Find a release roughly 3 years older.
          const newest = new Date(list[0].date);
          const target = new Date(newest);
          target.setFullYear(target.getFullYear() - 3);
          let bestIdx = list.length - 1;
          let bestDelta = Infinity;
          for (let i = 1; i < list.length; i++) {
            const delta = Math.abs(new Date(list[i].date) - target);
            if (delta < bestDelta) { bestDelta = delta; bestIdx = i; }
          }
          setLeftRelease(list[bestIdx]);
        }
      } catch (e) {
        if (cancelled) return;
        const msg = e.response?.data?.detail || 'Failed to load historical imagery list.';
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiCall]);

  // ----- Build a Wayback ImageMapType for a given release -----
  const makeWaybackMapType = useCallback((release) => {
    if (!release || !tileTemplate || !window.google?.maps) return null;
    return new window.google.maps.ImageMapType({
      name: `Wayback ${release.date}`,
      tileSize: new window.google.maps.Size(256, 256),
      maxZoom: 22,
      minZoom: 1,
      getTileUrl: (coord, zoom) => tileTemplate
        .replace('{release}', String(release.release_id))
        .replace('{z}', String(zoom))
        .replace('{y}', String(coord.y))
        .replace('{x}', String(coord.x)),
    });
  }, [tileTemplate]);

  // ----- Apply selected releases as the base imagery on each map -----
  // In SWIPE mode the bottom map shows leftRelease and the top (clipped) map
  // shows rightRelease. In FADE mode the bottom map shows whichever release
  // the chronological scrubber is currently parked on; the top map is hidden.
  useEffect(() => {
    const apply = (map, release) => {
      if (!map || !release) return;
      const type = makeWaybackMapType(release);
      if (!type) return;
      // Register the imagery as a named custom map type, then activate it.
      const key = `wayback_${release.release_id}`;
      try {
        map.mapTypes.set(key, type);
        map.setMapTypeId(key);
        // Wayback imagery is flat — kill the 45° tilt or tiles look wrong.
        map.setTilt(0);
      } catch (e) {
        console.warn('Failed to apply Wayback map type:', e);
      }
    };
    const bottomRelease = mode === 'fade' ? (currentScrub || leftRelease) : leftRelease;
    apply(leftMapRef.current, bottomRelease);
    // Only update the top map's imagery when it's actually visible.
    if (mode === 'swipe') apply(rightMapRef.current, rightRelease);
  }, [leftRelease, rightRelease, currentScrub, mode, makeWaybackMapType]);

  // ----- Keep the two maps perfectly in sync -----
  const wireSync = useCallback(() => {
    const a = leftMapRef.current;
    const b = rightMapRef.current;
    if (!a || !b || !window.google?.maps) return () => {};
    const syncFrom = (src, dst) => () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      try {
        const c = src.getCenter();
        if (c) dst.setCenter(c);
        const z = src.getZoom();
        if (z !== undefined) dst.setZoom(z);
      } finally {
        // Defer release so the listener on `dst` doesn't fire back immediately.
        setTimeout(() => { syncingRef.current = false; }, 0);
      }
    };
    const listeners = [
      a.addListener('center_changed', syncFrom(a, b)),
      a.addListener('zoom_changed', syncFrom(a, b)),
      b.addListener('center_changed', syncFrom(b, a)),
      b.addListener('zoom_changed', syncFrom(b, a)),
    ];
    return () => {
      listeners.forEach((l) => { try { l.remove(); } catch (_) { /* listener already removed */ } });
    };
  }, []);

  const onLeftLoad = useCallback((map) => {
    leftMapRef.current = map;
    // Strip tilt; Wayback ImageMapType doesn't render tilted.
    try { map.setTilt(0); } catch (_) { /* tilt API can be locked when MAP_ID is set */ }
    wireSync();
  }, [wireSync]);

  const onRightLoad = useCallback((map) => {
    rightMapRef.current = map;
    try { map.setTilt(0); } catch (_) { /* same — tilt safe to ignore on failure */ }
    wireSync();
  }, [wireSync]);

  // ----- Drag handle -----
  const handlePointerDown = (e) => {
    draggingRef.current = true;
    e.preventDefault();
  };
  useEffect(() => {
    const move = (e) => {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const pct = Math.max(2, Math.min(98, (x / rect.width) * 100));
      setSplitPct(pct);
    };
    const up = () => { draggingRef.current = false; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
  }, []);

  // ----- Close: copy current view back to underlying map -----
  const handleClose = () => {
    const m = leftMapRef.current || rightMapRef.current;
    if (m && underlyingMap) {
      try {
        const c = m.getCenter();
        if (c) underlyingMap.setCenter(c);
        const z = m.getZoom();
        if (z !== undefined) underlyingMap.setZoom(z);
      } catch (_) { /* underlying map may be unmounted — safe to ignore */ }
    }
    onClose?.();
  };

  // ----- Render -----
  return (
    <div
      ref={containerRef}
      className="absolute inset-0 z-40 bg-black"
      data-testid="historical-compare-overlay"
    >
      {/* LEFT (bottom) map — fills the whole container */}
      <div className="absolute inset-0">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={initialView.center}
          zoom={initialView.zoom}
          onLoad={onLeftLoad}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            keyboardShortcuts: false,
            gestureHandling: 'greedy',
            tilt: 0,
            // See WAYBACK_MAX_ZOOM comment above — block users from zooming
            // into a range Wayback returns non-image responses for.
            maxZoom: WAYBACK_MAX_ZOOM,
          }}
        />
      </div>

      {/* RIGHT (top) map — clipped to show only the right side in swipe mode;
          fully hidden in fade mode (the bottom map alone reflects the scrub date). */}
      <div
        className="absolute inset-0 pointer-events-auto"
        style={mode === 'swipe'
          ? { clipPath: `inset(0 0 0 ${splitPct}%)` }
          : { display: 'none' }}
        data-testid="historical-compare-right-clip"
      >
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={initialView.center}
          zoom={initialView.zoom}
          onLoad={onRightLoad}
          options={{
            disableDefaultUI: true,
            zoomControl: false,
            keyboardShortcuts: false,
            gestureHandling: 'greedy',
            tilt: 0,
            maxZoom: WAYBACK_MAX_ZOOM,
          }}
        />
      </div>

      {/* Vertical split handle — swipe mode only */}
      {mode === 'swipe' && (
        <div
          className="absolute top-0 bottom-0 z-10 pointer-events-none"
          style={{ left: `calc(${splitPct}% - 1px)` }}
          data-testid="historical-compare-handle-wrap"
        >
          <div className="h-full w-0.5 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.4)]" />
          <button
            type="button"
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
            className="pointer-events-auto absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white text-slate-900 shadow-xl flex items-center justify-center cursor-ew-resize border-2 border-slate-200 hover:scale-105 transition-transform"
            title="Drag to compare"
            aria-label="Drag to compare"
            data-testid="historical-compare-handle"
          >
            <ArrowsHorizontal size={20} weight="bold" />
          </button>
        </div>
      )}

      {/* Fade-mode chronological scrubber — drags the bottom map's imagery
          through every Wayback snapshot between the two chosen dates. */}
      {mode === 'fade' && scrubRange.length > 0 && (
        <div
          className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3 bg-card/95 backdrop-blur-sm border border-border rounded-full shadow-2xl px-4 py-2.5 max-w-[90vw] w-[500px]"
          data-testid="historical-compare-fade-slider-wrap"
        >
          {/* Play / Pause auto-advance toggle */}
          <button
            type="button"
            onClick={() => setIsPlaying((v) => !v)}
            disabled={scrubRange.length < 2}
            className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isPlaying ? 'bg-primary text-primary-foreground animate-pulse' : 'bg-secondary/70 hover:bg-secondary text-foreground/80'}`}
            title={isPlaying ? 'Pause auto-advance' : 'Play through time-lapse'}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            aria-pressed={isPlaying}
            data-testid="historical-timeline-playpause-btn"
          >
            {isPlaying
              ? <Pause size={12} weight="fill" />
              : <Play size={12} weight="fill" />}
          </button>
          <span className="text-[11px] font-mono shrink-0 opacity-70" data-testid="historical-fade-start-date">
            {fmtDate(scrubRange[0]?.date)}
          </span>
          <div className="relative flex-1">
            {/* Floating bubble showing the actual snapshot date under the thumb */}
            <div
              className="absolute -top-7 -translate-x-1/2 px-2 py-0.5 rounded bg-primary text-primary-foreground text-[10px] font-mono font-bold shadow-md whitespace-nowrap pointer-events-none"
              style={{ left: `${scrubRange.length > 1 ? (scrubIdx / (scrubRange.length - 1)) * 100 : 50}%` }}
              data-testid="historical-fade-current-date"
            >
              {fmtDate(currentScrub?.date)}
            </div>
            <input
              type="range"
              min={0}
              max={Math.max(0, scrubRange.length - 1)}
              step={1}
              value={scrubIdx}
              onChange={(e) => { setScrubIdx(Number(e.target.value)); if (isPlaying) setIsPlaying(false); }}
              className="w-full accent-primary cursor-pointer"
              aria-label="Scrub through historical imagery"
              data-testid="historical-compare-fade-slider"
            />
          </div>
          <span className="text-[11px] font-mono shrink-0 opacity-70" data-testid="historical-fade-end-date">
            {fmtDate(scrubRange[scrubRange.length - 1]?.date)}
          </span>
          {/* Export the current Timeline range as either an animated GIF or
              an H.264 MP4, or copy a 7-day shareable link to the clipboard.
              Clicking the Export button opens a popover menu so the user picks
              the action; everything is disabled while a request is in-flight. */}
          <div className="relative shrink-0 ml-1" ref={exportMenuRef}>
            <button
              type="button"
              onClick={() => { if (!exportState) setExportMenuOpen((v) => !v); }}
              disabled={scrubRange.length < 2 || !!exportState}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${exportState === 'done' ? 'bg-emerald-500 text-white' : 'bg-primary text-primary-foreground hover:opacity-90'}`}
              title="Export the time-lapse between these dates as MP4 or GIF, or copy a shareable link"
              aria-label="Export time-lapse"
              aria-haspopup="menu"
              aria-expanded={exportMenuOpen}
              data-testid="historical-timeline-export-btn"
            >
              {exportState && exportState !== 'done'
                ? <Spinner size={12} weight="bold" className="animate-spin" />
                : <DownloadSimple size={12} weight="bold" />}
              {exportState === 'done'
                ? 'Saved'
                : exportState
                  ? (exportState.format === 'share'
                      ? 'Creating link…'
                      : `Exporting ${(exportState.format || 'gif').toUpperCase()}…`)
                  : 'Export'}
              {!exportState && <CaretDown size={10} weight="bold" className="opacity-80" />}
            </button>
            {exportMenuOpen && !exportState && (
              <div
                role="menu"
                className="absolute bottom-full mb-1 right-0 w-44 bg-popover border border-border rounded-md shadow-2xl z-50 py-1"
                data-testid="historical-timeline-export-menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleExportMedia('mp4')}
                  className="w-full text-left px-3 py-1.5 text-xs font-semibold hover:bg-secondary/80 flex items-center justify-between"
                  data-testid="historical-timeline-export-mp4"
                >
                  <span>MP4</span>
                  <span className="text-[9px] uppercase tracking-wider opacity-60">video</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleExportMedia('gif')}
                  className="w-full text-left px-3 py-1.5 text-xs font-semibold hover:bg-secondary/80 flex items-center justify-between"
                  data-testid="historical-timeline-export-gif"
                >
                  <span>GIF</span>
                  <span className="text-[9px] uppercase tracking-wider opacity-60">image</span>
                </button>
                <div className="my-1 border-t border-border" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleCopyShareLink}
                  className="w-full text-left px-3 py-1.5 text-xs font-semibold hover:bg-secondary/80 flex flex-col gap-0.5"
                  title="Upload as MP4 and copy a public link (valid for 7 days) to the clipboard"
                  data-testid="historical-timeline-export-share"
                >
                  <span className="flex items-center gap-1.5">
                    <LinkSimple size={11} weight="bold" />
                    Copy share link
                  </span>
                  <span className="text-[9px] uppercase tracking-wider opacity-60 pl-4">MP4 · 7-day link</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleSendToClient}
                  className="w-full text-left px-3 py-1.5 text-xs font-semibold hover:bg-secondary/80 flex flex-col gap-0.5"
                  title="Generate the time-lapse and email the link straight to a client"
                  data-testid="historical-timeline-export-email"
                >
                  <span className="flex items-center gap-1.5">
                    <EnvelopeSimple size={11} weight="bold" />
                    Send to client
                  </span>
                  <span className="text-[9px] uppercase tracking-wider opacity-60 pl-4">MP4 · branded email</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top control bar: date pickers (left → vs → right) → mode toggle → close */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-card/95 backdrop-blur-sm border border-border rounded-xl shadow-2xl px-2.5 py-2 max-w-[95vw]" data-testid="historical-compare-toolbar">
        <Calendar size={16} weight="duotone" className="text-primary shrink-0 ml-1" />
        <DateSelect
          label="Left date"
          value={leftRelease}
          releases={releases}
          onChange={setLeftRelease}
          loading={loading}
          testId="historical-left-date-select"
        />
        <span className="text-muted-foreground text-xs font-medium px-1">vs</span>
        <DateSelect
          label="Right date"
          value={rightRelease}
          releases={releases}
          onChange={setRightRelease}
          loading={loading}
          testId="historical-right-date-select"
        />
        <div className="w-px h-5 bg-border" />
        {/* Mode toggle (Swipe / Fade) — placed after the right date picker per user request */}
        <div className="flex items-center bg-secondary/50 rounded-md p-0.5" role="group" aria-label="Compare mode" data-testid="historical-compare-mode-toggle">
          <button
            type="button"
            onClick={() => setMode('swipe')}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold transition-colors ${mode === 'swipe' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
            title="Swipe: drag a vertical divider to reveal each date"
            aria-pressed={mode === 'swipe'}
            data-testid="historical-mode-swipe-btn"
          >
            <ArrowsHorizontal size={12} weight="bold" /> Swipe
          </button>
          <button
            type="button"
            onClick={() => setMode('fade')}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold transition-colors ${mode === 'fade' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
            title="Timeline: scrub through every Wayback snapshot between the two dates"
            aria-pressed={mode === 'fade'}
            data-testid="historical-mode-fade-btn"
          >
            <FilmStrip size={12} weight="bold" /> Timeline
          </button>
        </div>
        <button
          type="button"
          onClick={recenterToInitialView}
          className="ml-1 flex items-center gap-1 px-2 py-1.5 rounded-md bg-secondary/70 hover:bg-secondary text-foreground/80 text-[11px] font-semibold transition-colors"
          title="Snap both maps back to where the property is centred"
          aria-label="Sync to current view"
          data-testid="historical-compare-sync-btn"
        >
          <Crosshair size={12} weight="bold" /> Sync
        </button>
        <button
          type="button"
          onClick={handleClose}
          className="ml-1 w-8 h-8 flex items-center justify-center rounded-md bg-secondary/70 hover:bg-secondary text-foreground/80 transition-colors"
          title="Close historical compare"
          aria-label="Close"
          data-testid="historical-compare-close"
        >
          <X size={15} weight="bold" />
        </button>
      </div>

      {/* Date labels overlaid on each side — arrows step to the previous / next release.
          `releases` is sorted newest first, so index+1 = older, index-1 = newer. */}
      {leftRelease && (
        <DateStepper
          release={leftRelease}
          releases={releases}
          onChange={setLeftRelease}
          align="left"
          testIdRoot="historical-left"
        />
      )}
      {rightRelease && (
        <DateStepper
          release={rightRelease}
          releases={releases}
          onChange={setRightRelease}
          align="right"
          testIdRoot="historical-right"
        />
      )}

      {/* Attribution — required by Esri ToS for Wayback layers */}
      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 z-10 text-[10px] text-white/80 bg-black/40 px-2 py-0.5 rounded">
        Imagery © Esri World Imagery Wayback
      </div>

      {/* Loading / error states */}
      {loading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/55 text-white text-sm" data-testid="historical-loading">
          <Spinner size={20} className="animate-spin mr-2" /> Loading historical imagery…
        </div>
      )}
      {error && !loading && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-30 bg-destructive text-destructive-foreground px-4 py-2 rounded-md text-sm max-w-md text-center" data-testid="historical-error">
          {error}
        </div>
      )}

      {/* QR code modal — opens after a successful Copy share link. Lets the
          estimator turn their laptop screen toward the homeowner during an
          on-site walkthrough; the homeowner scans the QR with their phone
          camera and instantly sees the 5-year time-lapse of their property. */}
      <ShareQrModal
        share={shareModal}
        projectName={projectName}
        onClose={() => setShareModal(null)}
      />

      {/* Send-to-client modal — captures recipient email + optional note,
          creates the share link and dispatches a branded SendGrid email so
          the estimator goes from "framed the time-lapse" → "client has the
          email" in a single keystroke. */}
      <SendToClientModal
        config={sendModal}
        apiCall={apiCall}
        onClose={() => setSendModal(null)}
      />
    </div>
  );
}
